import axiosInstance from '../utils/axiosConfig';

/**
 * Service for handling DocuWare-related API calls
 */

/**
 * Create a sick note document record in DocuWare
 * @param {number} employeeId - The employee ID
 * @param {string} sickNoteId - Unique sick note identifier
 * @param {string} sickNoteName - Filename of the sick note
 * @param {string} sickNoteDescription - Optional description
 * @returns {Promise} - Response with document_id
 */
export const createSickNoteRecord = async (employeeId, sickNoteId, sickNoteName, sickNoteDescription = '') => {
  try {
    const response = await axiosInstance.post('/api/docuware/sick-notes/create-record/', {
      employee_id: employeeId,
      sick_note_id: sickNoteId,
      sick_note_name: sickNoteName,
      sick_note_description: sickNoteDescription
    });
    return response.data;
  } catch (error) {
    console.error('Error creating sick note record:', error);
    throw error;
  }
};

/**
 * Upload a file to an existing sick note document record
 * @param {string} documentId - DocuWare document ID
 * @param {File} file - The file to upload
 * @returns {Promise} - Upload response
 */
export const uploadSickNoteFile = async (documentId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axiosInstance.post(
      `/api/docuware/sick-notes/upload-file/${documentId}/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error uploading sick note file:', error);
    throw error;
  }
};

/**
 * Download a sick note document
 * @param {string} documentId - DocuWare document ID
 * @param {string} filename - Filename for download
 * @returns {Promise} - Blob response
 */
export const downloadSickNote = async (documentId, filename) => {
  try {
    const response = await axiosInstance.get(
      `/api/docuware/sick-notes/download/${documentId}/`,
      {
        responseType: 'blob'
      }
    );
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return response.data;
  } catch (error) {
    console.error('Error downloading sick note:', error);
    throw error;
  }
};

/**
 * Fetch a sick note document as a blob (for in-app preview)
 * @param {string} documentId - DocuWare document ID
 * @returns {Promise} - Blob data for preview
 */
export const fetchSickNoteBlob = async (documentId) => {
  try {
    const response = await axiosInstance.get(
      `/api/docuware/sick-notes/download/${documentId}/`,
      {
        responseType: 'blob'
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching sick note preview:', error);
    throw error;
  }
};

/**
 * List sick note documents for an employee
 * @param {number} employeeId - The employee ID
 * @param {string} sickNoteId - Optional sick note ID filter
 * @returns {Promise} - List of documents
 */
export const listSickNoteDocuments = async (employeeId, sickNoteId = null) => {
  try {
    const params = {
      employee_id: employeeId
    };
    
    if (sickNoteId) {
      params.sick_note_id = sickNoteId;
    }
    
    const response = await axiosInstance.get('/api/docuware/sick-notes/documents/', {
      params
    });
    return response.data;
  } catch (error) {
    console.error('Error listing sick note documents:', error);
    throw error;
  }
};

/**
 * Delete a sick note document
 * @param {string} documentId - DocuWare document ID
 * @returns {Promise} - Delete response
 */
export const deleteSickNote = async (documentId) => {
  try {
    const response = await axiosInstance.delete(`/api/docuware/sick-notes/delete/${documentId}/`);
    return response.data;
  } catch (error) {
    console.error('Error deleting sick note:', error);
    throw error;
  }
};

/**
 * Complete sick note upload process (two-step: create record + upload file)
 * @param {number} employeeId - The employee ID
 * @param {string} sickNoteId - Unique sick note identifier
 * @param {File} file - The file to upload
 * @param {string} description - Optional description
 * @returns {Promise} - Complete upload response
 */
export const uploadSickNoteComplete = async (employeeId, sickNoteId, file, description = '') => {
  try {
    // Step 1: Create document record
    const createResponse = await createSickNoteRecord(
      employeeId,
      sickNoteId,
      file.name,
      description
    );
    
    const documentId = createResponse.document_id;
    
    // Step 2: Upload file to the created record
    const uploadResponse = await uploadSickNoteFile(documentId, file);
    
    return {
      documentId,
      sickNoteId,
      filename: file.name,
      ...uploadResponse
    };
  } catch (error) {
    console.error('Error in complete sick note upload:', error);
    throw error;
  }
};

/**
 * Upload sick note directly from My Files (not associated with leave request)
 * @param {File} file - The file to upload
 * @param {string} description - Optional description
 * @returns {Promise} - Complete upload response
 */
export const uploadSickNoteFromMyFiles = async (file, description = '') => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }
    
    const response = await axiosInstance.post(
      '/api/docuware/sick-notes/upload-direct/',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error uploading sick note from My Files:', error);
    throw error;
  }
};

/**
 * List sick notes formatted for My Files display
 * @returns {Promise} - Array of sick notes formatted for My Files
 */
export const listSickNotesForMyFiles = async () => {
  try {
    const response = await axiosInstance.get('/api/docuware/sick-notes/list-for-my-files/');
    return response.data;
  } catch (error) {
    console.error('Error listing sick notes for My Files:', error);
    throw error;
  }
};

export default {
  createSickNoteRecord,
  uploadSickNoteFile,
  downloadSickNote,
  fetchSickNoteBlob,
  listSickNoteDocuments,
  deleteSickNote,
  uploadSickNoteComplete,
  uploadSickNoteFromMyFiles,
  listSickNotesForMyFiles
};

